import { ReactElement, useEffect, useState } from 'react';
import {
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISubNav,
    UIText,
    useUINav,
    UIAnimation
} from '@/ui';
import {
    LwdSubscriptionSettingsSection,
    NewSubscriptionSettingsSection,
    SmsSubscriptionSettingsSection,
    SubscriptionSettingsSection
} from '@/companies/components';
import { useCompanyContext } from '@/companies/providers';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';
import { Navigate, useSearchParams } from 'react-router';
import confetti from '../assets/lotties/confetti.json';

export function BillingSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            {
                text: t('shared.labels.settings')
            },
            { text: t('shared.labels.plan_and_billing') }
        ]);
    }, [setParts, t]);

    const { company } = useCompanyContext();
    const { subscription, lwdSubscription, smsSubscription } = company;
    const { canViewBilling, canEditBilling } = useCompanyPermissions();

    const [searchParams] = useSearchParams();

    const subscriptionSuccess = searchParams.get('subscription') === 'success';

    const [showSubscriptionSuccess, setShowSubscriptionSuccess] =
        useState(subscriptionSuccess);

    if (!canViewBilling) {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    padding: 32,
                    gap: 24
                }}
            >
                <UISubNav title={t('shared.labels.plan_and_billing')} />
                {subscription ? (
                    <SubscriptionSettingsSection
                        subscription={subscription}
                        canEdit={canEditBilling}
                    />
                ) : (
                    <NewSubscriptionSettingsSection canEdit={canEditBilling} />
                )}

                {lwdSubscription && (
                    <LwdSubscriptionSettingsSection
                        subscription={lwdSubscription}
                        canEdit={canEditBilling}
                    />
                )}

                {subscription && (
                    <SmsSubscriptionSettingsSection
                        smsSubscription={smsSubscription}
                        canEdit={canEditBilling}
                    />
                )}
            </div>
            {subscriptionSuccess && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        pointerEvents: 'none',
                        zIndex: 999999
                    }}
                >
                    <UIAnimation animationData={confetti} loop={false} />;
                </div>
            )}
            <UIModal
                isOpen={showSubscriptionSuccess}
                onClose={() => {
                    setShowSubscriptionSuccess(false);
                }}
            >
                <UIModalHeader
                    title={t('pages.billing_settings.subscribed_title')}
                />
                <UIModalContent>
                    <UIText>
                        {t('pages.billing_settings.subscribed_message')}
                    </UIText>
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            text: t('shared.labels.close'),
                            onClick: () => {
                                setShowSubscriptionSuccess(false);
                            }
                        }
                    ]}
                />
            </UIModal>
        </>
    );
}
